<script setup lang="ts">
import store from "@/store";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";

import {
	planTermList,
	planTermCreate,
	planTermDelete,
} from "@/common/api/rest/plan/term";

import { termList } from "@/common/api/rest/term/term";

import {
	breadcrumbSimple,
	baseConfirm,
	baseModal,
	baseTable,
	iconMenu,
	svgIcon,
} from "@/common/components";

import BaseSearch from "@/components/search/base-search.vue";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const tableData = ref<any[]>([]);
const selected = ref<any>(null);
const payload = ref<any>(null);

const limit = ref<number>(6);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const loading = ref(false);

const terms = ref<any[]>([]);
const termSearch = ref<string>("");
const termSearched = ref(false);
const termLoading = ref(false);
const termLimit = ref<number>(6);
const termPage = ref<number>(0);
const termPages = ref<number>(0);
const termSelected = ref<any>(null);

const planId = computed(() => route.params.plan as string);

const breadcrumbList = computed(() => [
	{
		key: "register",
		label: t("common.labels.registrations"),
		url: "/register",
	},
	{
		key: "plans",
		label: t("common.labels.plans"),
		url: "/register/plan",
	},
	{
		key: "edit",
		label: t("common.labels.edit"),
	},
]);

const menuList = computed(() => [
	{
		key: "infos",
		icon: "field",
		label: t("collection.labels.infos"),
		url: `/register/plan/${planId.value}/edit`,
	},
	{
		key: "price",
		icon: "no-image",
		label: "Preços",
		url: `/register/plan/${planId.value}/price`,
	},
	{
		key: "term",
		icon: "no-image",
		label: "Termos",
		url: `/register/plan/${planId.value}/term`,
	},
	{
		key: "module",
		icon: "no-image",
		label: "Módulos",
		url: `/register/plan/${planId.value}/module`,
	},
]);

const tableHeader = computed(() => [
	{ key: "code", label: t("common.labels.code") },
	{ key: "name", label: t("common.labels.name") },
	{ key: "status", label: t("common.labels.status") },
]);

const statusOptions = computed(() => ({
	ACTIVE: t("common.labels.active"),
	INACTIVE: t("common.labels.inactive"),
}));

async function changeMenu(url: string) {
	router.push(url);
}

function back() {
	router.push(`/register/plan`);
}

function fetchTerms(value?: string) {
	termSearch.value = value ?? "";

	termList({
		page: termPage.value,
		limit: termLimit.value,
		search: termSearch.value,
	})
		.then((response) => {
			const { success, data, message } = response;
			if (success) {
				terms.value = data.list.map((item) => ({
					id: item.term_id,
					label: item.name,
					value: item.term_id,
				}));
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});
}

function fetchData() {
	planTermList({
		plan_id: planId.value,
		page: page.value,
		limit: limit.value,
		search: search.value,
	})
		.then((response) => {
			const { success, data, message } = response;
			if (success) {
				tableData.value = data.list;
				pages.value = data.infos.pages;
				total.value = data.infos.total;
				searched.value = data.infos.count;
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});
}

function changePage(value: number) {
	page.value = value;
	fetchData();
}

function searchItem(value: string) {
	search.value = value;
	fetchData();
}

function addItem() {
	showModalAdd.value = true;
	termSearched.value = true;
}

function addItemCancel() {
	showModalAdd.value = false;
	termSearched.value = false;
	payload.value = null;
}

function selectTerm(option: any) {
	termSelected.value = option;
}

function addItemConfirm() {
	store.commit("SET_LOADING", true);

	const payload = {
		plan_id: planId.value,
		term_id: termSelected.value.value,
	};

	planTermCreate(payload)
		.then((response) => {
			const { success, message } = response;

			if (success) {
				toast({ message: message, type: "success" });
				addItemCancel();
				fetchData();
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

function removeItem(data: any) {
	selected.value = data;
	showModalDel.value = true;
}

function removeItemCancel() {
	showModalDel.value = false;
	selected.value = null;
}

function removeItemConfirm() {
	if (!selected.value) return;

	planTermDelete(selected.value.item_id)
		.then((response) => {
			const { success, message } = response;

			if (success) {
				toast({ message: message, type: "success" });
				removeItemCancel();
				fetchData();
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

onMounted(() => {
	fetchData();
	fetchTerms();
});
</script>

<template>
	<article class="register-page">
		<section class="page-header">
			<breadcrumb-simple
				:items="breadcrumbList"
				:show-home="true"
				url-home="/home"
				active="edit"
			/>
		</section>

		<section class="page-content">
			<div class="tab-header">
				<div class="title">
					<svg-icon icon="plans" size="2rem" />
					{{ t("plan.labels.plan_edit") }}
				</div>

				<div class="actions">
					<button class="btn btn-default" @click="back()">
						<svg-icon icon="close" size="1rem" />
					</button>
				</div>
			</div>

			<div class="tab-content">
				<icon-menu
					:tabs="menuList"
					active="term"
					size="3rem"
					@selected="changeMenu($event)"
				/>

				<base-table
					:header="tableHeader"
					:data="tableData"
					:limit="limit"
					:page="page"
					:pages="pages"
					:total="total"
					:searched="searched"
					:loading="loading"
					:options="true"
					:del="true"
					:edit="true"
					@change:page="changePage($event)"
					@searching="searchItem($event)"
					@btn:add="addItem()"
					@btn:del="removeItem($event)"
				>
					<template #status="{ data }">
						{{ statusOptions[data.status] }}
					</template>
				</base-table>

				<base-modal
					class="model-search"
					title="ADICIONAR TERMO"
					button-confirm="Adicionar"
					button-close="Cancelar"
					:show="showModalAdd"
					:show-button-confirm="true"
					@close="addItemCancel()"
					@confirm="addItemConfirm()"
				>
					<template #content>
						<BaseSearch
							label="Buscar Termo"
							placeholder="Ex: Termo de uso"
							:search="termSearch"
							:searched="termSearched"
							:loading="termLoading"
							:options="terms"
							optionLabel="label"
							@search="fetchTerms"
							@select="selectTerm"
						/>
					</template>
				</base-modal>

				<base-confirm
					:show="showModalDel"
					title="Remover Termo"
					:message="`Você deseja remover o termo <strong>${selected?.name}</strong>?<br/>Esta ação não pode ser desfeita.`"
					@cancel="removeItemCancel()"
					@confirm="removeItemConfirm()"
				/>
			</div>
		</section>
	</article>
</template>

<style lang="scss" scoped>
.register-page {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	.page-header {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		width: inherit;
		background: white;
		border-radius: 0.5rem;
		padding-inline: 1rem;
		padding-block: 0.5rem;
	}

	.page-content {
		display: flex;
		flex-direction: column;
		background: white;
		border-radius: 0.5rem;

		.tab-header {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;
			gap: 0.5rem;
			padding-inline: 1.25rem;
			padding-block: 0.75rem;
			margin-block-end: 1.5rem;
			border-block-end: 1px solid var(--border);

			.title {
				display: flex;
				flex-direction: row;
				align-items: center;
				gap: 0.5rem;
				font-size: 1.2rem;
				text-transform: uppercase;
			}

			.actions {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-end;
				gap: 0.75rem;

				button {
					width: 1.75rem;
					height: 1.75rem;
					padding: 0;
				}
			}
		}

		.tab-content {
			display: flex;
			flex-direction: column;
			gap: 1.54rem;
			padding-inline: 1.25rem;
			padding-block-end: 1.25rem;

			&-actions {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-end;
				gap: 1rem;
			}

			&-title {
				font-size: 1.25rem;
				font-weight: 600;
				text-transform: uppercase;
				color: var(--text-medium);
				margin-block-end: 0.75rem;
			}

			&-list {
				display: grid;
				grid-template-columns: repeat(2, minmax(200px, 1fr));
				gap: 1rem;

				&-item {
					flex: 1 1 200px;
					display: flex;
					flex-direction: column;
					gap: 0.75rem;
					border: 2px solid var(--border);
					border-radius: 0.5rem;
					padding-block: 1rem;
					padding-inline: 1.25rem;
					cursor: pointer;

					&:hover {
						border-color: var(--primary);
					}

					&-title {
						font-size: 1.25rem;
						font-weight: 600;
						text-transform: uppercase;
					}

					&-description {
						font-size: 1rem;
						color: var(--text-light);
					}
				}
			}
		}
	}

	:deep(.modal .modal-body .content) {
		height: 200px;
	}
}
</style>
